import { Box } from '@jotta/ui/Box'
import { PlainBrandIcon } from '@jotta/ui/BrandIcon'
import { Button } from '@jotta/ui/Button'
import { Flex } from '@jotta/ui/Flex'
import { RadixDialog } from '@jotta/ui/RadixDialog'
import { RadixModal } from '@jotta/ui/RadixModal'
import { Trans, t } from '@lingui/macro'
import { observer } from 'mobx-react-lite'
import { useCallback, useEffect, useState } from 'react'
import type { FileUploadAction } from '../ActionManager/FileUploadAction'
import type { FileUploadManager } from '../ActionManager/FileUploadManager'
import { useFileStore } from '../FileStore/FileContext'
import type { FolderStore } from '../Folder/FolderStore'
import SvgWarningCircle from '@jotta/ui/SvgWarningCircle'

const FileUploadErrorRow = observer<{
  upload: FileUploadAction
  folder: FolderStore
}>(function FileUploadErrorRow({ upload, folder }) {
  let message = upload.error?.message
  const folderName = folder.pathItem.name

  if (upload.isInsufficientCapacityError) {
    if (folder.pathItem.isSharedFolder) {
      message = t`Could not upload file as the owner of the shared folder "${folderName}" does not have enough storage space`
    } else {
      message = t`Could not upload file as you do not have enough storage space`
    }
  }
  return (
    <tr>
      <td>{upload.name}</td>
      <td>{message}</td>
    </tr>
  )
})

export const FileUploadErrorDialog = observer<{ manager: FileUploadManager }>(
  function FileUploadErrorDialog({ manager }) {
    const [open, setOpen] = useState(false)
    const items = manager.failedActions
    const folder = useFileStore().folder

    const close = useCallback(() => {
      for (const upload of manager.failedActions) {
        upload.remove()
      }
      if (!manager.actions.length) {
        manager.clear()
        manager.open = false
      }
    }, [manager])

    const retry = useCallback(() => {
      for (const upload of manager.failedActions) {
        upload.retry()
      }
    }, [manager])

    useEffect(() => {
      if (items.length) {
        setOpen(true)
      } else {
        setOpen(false)
      }
    }, [items])

    return (
      <RadixModal open={open} variant="dialogs.overlayWide">
        <RadixDialog
          data-testid="FileUploadErrorDialog"
          title={t`Error`}
          customButtons={
            <Box variant="dialogs.footer">
              <Button
                className="btn btn-outline"
                onClick={close}
              >{t`Close`}</Button>
              <Button
                className="btn btn-primary"
                onClick={retry}
              >{t`Retry`}</Button>
            </Box>
          }
        >
          <Box>
            <Flex wrap={false} sx={{ gap: '2' }}>
              <Box sx={{ width: '24px', height: '24px', color: 'danger' }}>
                <PlainBrandIcon icon={SvgWarningCircle} />
              </Box>
              <span>
                <Trans>One or more files could not be uploaded</Trans>
              </span>
            </Flex>

            <table
              sx={{
                mt: 4,
                display: 'block',
                maxHeight: '40vh',
                overflowY: 'auto',
                boxShadow:
                  '0 -4px 5px -5px rgba(0, 0, 0, 0.3), 0 4px 5px -5px rgba(0, 0, 0, 0.3)',
                '& th': {
                  fontWeight: '500',
                  py: 2,
                },
                '& th:first-child, & td:first-child': {
                  pr: 2,
                },
                '& th:last-child, & td:last-child': {
                  pl: 2,
                },
                '& tr': {
                  verticalAlign: 'top',
                },
                '& tbody tr:not(:last-child)': {
                  borderBottom: '1px solid rgb(var(--rgb-border) / 0.5)',
                },
                '& thead': {
                  position: 'sticky',
                  top: 0,
                  background: 'white',
                  boxShadow: '0 4px 5px -5px rgba(0, 0, 0, 0.3)',
                },
                '& td': {
                  py: 2,
                },
              }}
            >
              <thead>
                <tr>
                  <th>{t`Filename`}</th>
                  <th>{t`Error`}</th>
                </tr>
              </thead>
              <tbody>
                {items.slice(0, 100).map(upload => (
                  <FileUploadErrorRow
                    folder={folder}
                    upload={upload}
                    key={upload.id}
                  />
                ))}
              </tbody>
            </table>
          </Box>
        </RadixDialog>
      </RadixModal>
    )
  },
)
